<template>
  <div ref="cal"></div>

  <Dialog
    v-model:visible="modalOptions.show"
    position="middle"
    :modal="true"
    :autoZIndex="false"
    :dismissableMask="true"
    :closeOnEscape="false"
    contentClass="event-modal"
  >
    <event-form
      :initialDate="modalOptions.initialDate"
      :action="modalOptions.action"
      :existingEvent="modalOptions.existingEvent"
      @save-event="saveEvent"
      @delete-event="deleteEvent"
    ></event-form>
  </Dialog>
</template>

<script>
import { ref, toRefs, watch, onMounted } from "vue";
import EventForm from "../components/forms/EventForm";
import useEvents from "../composables/events.js";
import { Calendar } from "@fullcalendar/core";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import interactionPlugin from "@fullcalendar/interaction";
import frLocale from "@fullcalendar/core/locales/fr";
import moment from "moment";

export default {
  props: {
    projectId: {
      required: true,
      type: String
    },
    size: {
      required: true,
      type: String,
      validator: value => {
        return value === "widget" || value === "expand";
      }
    },
    update: {
      required: false
    }
  },
  components: {
    EventForm
  },
  emits: ["event-added", "event-removed", "event-modified"],
  setup(props, context) {
    const cal = ref(null);
    const {
      events: use_events,
      setRange: use_setRange,
      rangeStart: use_rangeStart,
      rangeEnd: use_rangeEnd,
      fetchEvents: use_fetchEvents,
      saveEvent: use_saveEvent,
      updateEvent: use_updateEvent,
      deleteEvent: use_deleteEvent
    } = useEvents();

    const initialDate = moment().format();
    const initialView = props.size === "expand" ? "dayGridMonth" : "listWeek";
    const headerToolbar =
      props.size === "expand"
        ? {
            left: "prev,next",
            center: "title",
            right: "dayGridMonth,dayGridWeek,timeGridDay"
          }
        : false;

    const modalOptions = ref({
      show: false,
      initialDate: null,
      action: null
    });

    const fullCal = ref(null);

    // For fetching events by dates in DB
    const monthsRange = 6;

    onMounted(() => {
      initCalendar();
    });

    if (props.size === "widget") {
      const propsRefs = toRefs(props);
      const update = propsRefs.update;

      watch(update, updateData => {
        switch (updateData.action) {
          case "add":
            fullCal.value.addEvent(updateData.event);
            break;
          case "modify":
            removeEvent(updateData.event.id);
            fullCal.value.addEvent(updateData.event);
            break;
          case "remove":
            removeEvent(updateData.id);
            break;
        }
      });
    }

    async function initCalendar() {
      fullCal.value = new Calendar(cal.value, {
        events: use_events,
        plugins: [dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin],
        locale: frLocale,
        initialView: initialView,
        showNonCurrentDates: true,
        initialDate: initialDate,
        headerToolbar: headerToolbar,
        editable: false,
        height: "100%",
        datesSet: function(info) {
          getEventsByDate(info);
        },
        dateClick: function(info) {
          modalOptions.value = {
            show: true,
            initialDate: info.date,
            action: "create"
          };
        },
        eventClick: function(info) {
          const existing = use_events.find(item => item.id === info.event.id);
          modalOptions.value = {
            show: true,
            action: "modify",
            existingEvent: existing
          };
        }
      });
      fullCal.value.render();

      setTimeout(() => {
        fullCal.value.updateSize();
      }, 100);
    }

    async function getEventsByDate(info) {
      const viewEnd = moment(info.end)
        .add(-1, "days")
        .toDate();

      const viewStart = info.start;
      if (!use_rangeStart.value || !use_rangeEnd.value) {
        use_setRange(info.start, viewEnd, monthsRange);

        // Fetch events from db from use_rangeStart.value until use_rangeEnd.value
        await fetchEvents({
          start: use_rangeStart.value,
          end: use_rangeEnd.value
        });
      } else {
        if (moment(viewEnd).isAfter(use_rangeEnd.value)) {
          const fetchStart = moment(use_rangeEnd.value)
            .add(1, "days")
            .toDate();

          const addDays = 1;
          use_setRange(null, use_rangeEnd.value, monthsRange, addDays);

          // Fetch the relevant events from DB from fetchStart until use_rangeEnd.value
          await fetchEvents({
            start: fetchStart,
            end: use_rangeEnd.value
          });
        }

        if (moment(viewStart).isBefore(use_rangeStart.value)) {
          const fetchStart = moment(use_rangeStart.value)
            .add(-monthsRange, "months")
            .add(-1, "days")
            .toDate();

          const fetchEnd = moment(use_rangeStart.value)
            .add(-1, "days")
            .toDate();

          // Fetch events from DB from fetchStart until fetchEnd
          await fetchEvents({
            start: fetchStart,
            end: fetchEnd
          });

          use_setRange(moment(fetchStart).toDate(), null, null);
        }
      }
    }

    function toggleNavButtons(action) {
      const nextBtn = document.getElementsByClassName("fc-next-button")[0];
      const prevBtn = document.getElementsByClassName("fc-prev-button")[0];

      nextBtn.classList[action]("no-click");
      prevBtn.classList[action]("no-click");
    }

    async function fetchEvents(datesRange) {
      toggleNavButtons("add");
      const events = await use_fetchEvents(props.projectId, datesRange);
      toggleNavButtons("remove");
      if (events.length) {
        events.forEach(event => {
          const formattedEvent = formatDatesforCal(event);
          fullCal.value.addEvent(formattedEvent);
        });
      }
    }

    function formatDatesforCal(event) {
      const startDate = event.start.seconds * 1000;
      let endDate;
      if (event.end) {
        endDate = event.end.seconds * 1000;
      }
      if (event.time) {
        event.start = moment(startDate).toDate();
        if (endDate) {
          event.end = moment(endDate).toDate();
        }
      } else {
        event.start = moment(startDate).format("YYYY-MM-DD");
        if (endDate) {
          event.end = moment(endDate).format("YYYY-MM-DD");
        }
      }

      return event;
    }

    async function deleteEvent(eventId) {
      await use_deleteEvent(props.projectId, eventId);
      removeEvent(eventId);
      context.emit("event-removed", eventId);
      resetModalOptions();
    }

    function removeEvent(id) {
      const calEvent = fullCal.value.getEventById(id);
      calEvent.remove();
    }

    async function saveEvent(formEvent) {
      const eventToSave = {
        id: formEvent.id ? formEvent.id : null,
        title: formEvent.title ? formEvent.title : "Event",
        fullDay: formEvent.fullDay,
        rangeDays: formEvent.rangeDays,
        start: null,
        end: null,
        time: false,
        address: formEvent.address,
        notes: formEvent.notes
      };

      if (formEvent.dates.length && formEvent.dates[1]) {
        // Event spans multiple days

        if (formEvent.startTime) {
          eventToSave.start = setTime(formEvent, "startTime", { index: 0 });
        } else {
          eventToSave.start = formEvent.dates[0];
        }

        if (formEvent.endTime) {
          eventToSave.end = setTime(formEvent, "endTime", { index: 1 });
        } else {
          eventToSave.end = moment(formEvent.dates[1])
            .add(1, "days")
            .toDate();
        }
      } else {
        // Event on single day
        if (formEvent.startTime) {
          eventToSave.start = setTime(formEvent, "startTime");
        } else {
          eventToSave.start = formEvent.dates;
        }

        if (formEvent.endTime) {
          eventToSave.end = setTime(formEvent, "endTime");
        }
      }

      if (formEvent.startTime || formEvent.endTime) {
        eventToSave.time = true;
      } else {
        eventToSave.time = false;
      }

      if (eventToSave.id) {
        // modify existing event
        await use_updateEvent(props.projectId, eventToSave);
        removeEvent(eventToSave.id);
        context.emit("event-modified", eventToSave);
        if (!eventToSave.time) {
          eventToSave.start = moment(eventToSave.start).format("YYYY-MM-DD");
          if (eventToSave.end) {
            eventToSave.end = moment(eventToSave.end).format("YYYY-MM-DD");
          }
        }

        //Only add event to cal if its new date is included in the already loaded dates
        if (checkEventWithinRange(eventToSave)) {
          fullCal.value.addEvent(eventToSave);
        }
      } else {
        // save new event
        await use_saveEvent(props.projectId, eventToSave);
        context.emit("event-added", eventToSave);
        if (!eventToSave.time) {
          eventToSave.start = moment(eventToSave.start).format("YYYY-MM-DD");
          if (eventToSave.end) {
            eventToSave.end = moment(eventToSave.end).format("YYYY-MM-DD");
          }
        }

        fullCal.value.addEvent(eventToSave);
      }

      resetModalOptions();
    }

    // Check if events should be fetched based on range of dates
    function checkEventWithinRange(event) {
      const rangeStartMinusOne = moment(use_rangeStart.value).add(-1, "days");
      const rangeEndPlusOne = moment(use_rangeEnd.value).add(1, "days");
      return moment(event.start).isBetween(rangeStartMinusOne, rangeEndPlusOne);
    }

    function setTime(formEvent, timeType, range = null) {
      const time = {
        hour: moment(formEvent[timeType]).get("hour"),
        minute: moment(formEvent[timeType]).get("minute")
      };

      if (range) {
        return moment(formEvent.dates[range.index])
          .set(time)
          .toDate();
      } else {
        return moment(formEvent.dates)
          .set(time)
          .toDate();
      }
    }

    function resetModalOptions() {
      modalOptions.value = {
        show: false,
        initialDate: null,
        action: null
      };
    }

    return {
      cal,
      modalOptions,
      saveEvent,
      deleteEvent
    };
  }
};
</script>
